import React, {useContext, useMemo, useReducer, useState} from 'react'
import {makeStyles, TableCell, TableRow} from '@material-ui/core';
import PropTypes from 'prop-types';
import Tr from 'translations/Translations';
import moment from "libs/moment";
import WorkableIconDeleteButton from 'components/button/WorkableIconDeleteButton';
import WorkableIconDownloadButton from 'components/button/WorkableIconDownloadButton';
import WorkableIconDownloadCountButton from "components/button/WorkableIconDownloadCountButton";
import WarningRoundedButton from "components/button/WarningRoundedButton";
import DateFormats from "constants/DateFormats";
import SiwzService from "services/SiwzService";
import DemandValidationContext from "context/DemandValidationContext";
import WorkableIconExpandButton from "components/button/WorkableIconExpandButton";
import iconWorksReducer from "libs/iconWorksReducer";
import WorkableIconUnpublishButton from "components/button/WorkableIconUnpublishButton";
import WorkableIconPublishButton from "components/button/WorkableIconPublishButton";
import WorkableIconSearchButton from "components/button/WorkableIconSearchButton";
import SiwzVersionsTable from "./SiwzVersionsTable";
import RfxOfferService from "services/RfxOfferService";
import reduxStore from 'redux/store'
import StatisticsService from "services/StatisticsService";
import {DemandValidation} from "constants/DemandValidation";
import validateSection from "services/helpers/validateSection";
import WorkableIconShareButton from "components/button/WorkableIconShareButton";
import ShareSelectPopup, {sharePopupData} from "components/popup/ShareSelectPopup";
import useSnackbar from "hooks/useSnackbar";
import HelpComponent from "../../../HelpComponent";
import WorkableIconEditButton from "../../../button/WorkableIconEditButton";

const useStyle = makeStyles(theme => ({
    warnIcon: {
        padding: 0,
        color: `${theme.palette.secondary.main} !important`
    },
    descriptionTooltip: {
        whiteSpace: 'pre-line'
    },
    buttonColumn: theme.components.table.buttonColumn,
    versionsCell: {
        background: theme.palette.background.default,
        paddingLeft: 0,
        paddingRight: 0
    }
}));

export const getPublishDateCellValue = (row) => {
    switch (row.status) {
        case 'uploaded':
            return Tr('new');
        case 'unpublished':
            return Tr('unpublished');
        default:
            return row.publishDate ? moment(row.publishDate).format(DateFormats.DATETIME_FORMAT) : null
    }
};

function SiwzFileTableRow(
    {
        demandId,
        index,
        row,
        isPublished,
        disabled,
        onOpenDownloadHistory,
        onEditDescriptionFile,
        onEditNoticeReferenceNumber,
        showShare,
    }) {
    const classes = useStyle();
    const currentDemandValidation = useContext(DemandValidationContext);
    const [shareFilePopup, setShareFilePopup] = useState(null);
    const {enqueueSuccessSnackbar} = useSnackbar();

    const [iconWorks, dispatchIconWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        download: false,
        publish: false,
        delete: false,
        expand: false,
        share: false,
        enterNoticeReferenceNumber: false
    });

    const hasVersions = useMemo(() => row.version && row.version > 1, [row]);

    const onVersionsExpand = () => {
        dispatchIconWorks({type: 'expand', works: true});
        var func;
        if (!row.versions)
            func = SiwzService.getVersions
        else
            func = SiwzService.clearVersions

        func(demandId, SiwzService.buildSiwzIdentity(row.identity, row.source))
            .finally(() => dispatchIconWorks({type: 'expand', works: false}));
    }

    const onDownload = () => {
        dispatchIconWorks({type: 'download', works: true});
        SiwzService.getDownloadFile(demandId, SiwzService.buildSiwzIdentity(row.identity, row.source), false)
            .finally(() => dispatchIconWorks({type: 'download', works: false}));
    }

    const checkRealizationAmountValue = async () => {
        //demand validation nie ma na II etapie
        if (!currentDemandValidation && row.type === "x.realizationAmountValue") {
            const {resources} = reduxStore.getState();
            const currentRfxRound = resources[StatisticsService.CURRENT_RFX_RESOURCE_ID];
            if (Boolean(currentRfxRound)) {
                RfxOfferService.getOffersDecryptionStatus(currentRfxRound.id);
            }
        } else if (currentDemandValidation === DemandValidation.DOCUMENTS && row.type.startsWith('x.openOffers')) {
            await validateSection(DemandValidation.CONTRACTOR_VERIFICATION, demandId, false)
            await validateSection(DemandValidation.OFFERS_EVALUATION, demandId, false)
            await validateSection(DemandValidation.OFFER_CHOICE, demandId, false)
        }
    }

    const onPublish = () => {
        dispatchIconWorks({type: 'publish', works: true});
        SiwzService.publish(demandId, SiwzService.buildSiwzIdentity(row.identity, row.source), currentDemandValidation)
            .then(checkRealizationAmountValue).finally(() => dispatchIconWorks({type: 'publish', works: false}));
    };

    const onUnpublish = () => {
        dispatchIconWorks({type: 'publish', works: true});
        SiwzService.unpublish(demandId, SiwzService.buildSiwzIdentity(row.identity, row.source), currentDemandValidation)
            .then(checkRealizationAmountValue).finally(() => dispatchIconWorks({type: 'publish', works: false}));
    };

    const onDelete = () => {
        dispatchIconWorks({type: 'delete', works: true});
        SiwzService.deleteItem(demandId, SiwzService.buildSiwzIdentity(row.identity, row.source), currentDemandValidation)
            .finally(() => {
                dispatchIconWorks({type: 'delete', works: false})
            });
    }

    const onShareFile = (row) => setShareFilePopup(sharePopupData(row.name, row));

    const doShare = (row, system) => {
        setShareFilePopup(null);
        dispatchIconWorks({type: 'share', works: true});
        SiwzService.shareSiwz(demandId, SiwzService.buildSiwzIdentity(row.identity, row.source), system).then((resp) => {
            if (resp)
                enqueueSuccessSnackbar(Tr("int_upload_success_x", {x: system}, true))
        }).finally(() => {
            dispatchIconWorks({type: 'share', works: false})
        });
    }

    return (
        <>
            <TableRow>
                <TableCell>{index}</TableCell>
                <TableCell>
                    {(isPublished && row.status !== 'published' && row.status !== 'accepted') &&
                        <WarningRoundedButton className={classes.warnIcon} disabled tooltip={Tr('file_not_published')}/>
                    } {row.name}
                </TableCell>
                <TableCell className={classes.buttonColumn}>
                    <WorkableIconSearchButton
                        onClick={() => onEditDescriptionFile(row)}
                        tooltip={row.description ? <p className={classes.descriptionTooltip}>{row.description}</p> : <i>{Tr('no_description')}</i>}
                    />
                </TableCell>
                <TableCell>{row.typeTranslated}</TableCell>
                <TableCell>
                    <HelpComponent text={`${row.fileSize}, SHA256: ${row.sha256}`}></HelpComponent>
                </TableCell>
                <TableCell>{moment(row.createDate).format(DateFormats.DATETIME_FORMAT)}</TableCell>
                {isPublished && <TableCell>{getPublishDateCellValue(row)}</TableCell>}
                <TableCell className={classes.buttonColumn}>
                    {row.actions.enterNoticeReferenceNumber &&
                        <WorkableIconEditButton
                            tooltip={Tr('enter_notice_reference_number')}
                            onClick={() => onEditNoticeReferenceNumber(row)}
                            disabled={iconWorks.anyWorks}
                            hideTooltip={!row.actions.enterNoticeReferenceNumber}
                            working={iconWorks.enterNoticeReferenceNumber}
                        />
                    }
                </TableCell>
                <TableCell className={classes.buttonColumn}>
                    <WorkableIconDownloadButton
                        onClick={() => onDownload(row.actions.download, row.name)}
                        disabled={iconWorks.anyWorks || !row.actions.download}
                        working={iconWorks.download}
                    />
                </TableCell>
                {
                    showShare &&
                    <TableCell className={classes.buttonColumn}>
                        <WorkableIconShareButton
                            onClick={() => onShareFile(row)}
                            hideTooltip={!row.actions.download}
                            disabled={iconWorks.anyWorks || !row.actions.download}
                            working={iconWorks.share}
                        />

                        {!!shareFilePopup && <ShareSelectPopup
                            data={shareFilePopup}
                            onCancel={() => setShareFilePopup(null)}
                            onChoose={doShare}
                        />}
                    </TableCell>
                }
                {isPublished && <TableCell className={classes.buttonColumn}>

                    {row.status === "published" || row.status === "accepted" ?
                        <WorkableIconUnpublishButton
                            onClick={onUnpublish}
                            disabled={iconWorks.anyWorks || !row.actions.unpublish}
                            hideTooltip={!row.actions.unpublish}
                            working={iconWorks.publish}
                        />
                        :
                        <WorkableIconPublishButton
                            onClick={onPublish}
                            hideTooltip={!row.actions.publish}
                            disabled={iconWorks.anyWorks || !row.actions.publish}
                            working={iconWorks.publish}
                        />
                    }
                </TableCell>
                }
                {isPublished && <TableCell className={classes.buttonColumn}>
                    <WorkableIconDownloadCountButton
                        tooltip={Tr("download_count_x", {downloadCount: row.downloadCount})}
                        onClick={() => onOpenDownloadHistory(row)}
                        hideTooltip={!row.actions.downloadDetails}
                        disabled={iconWorks.anyWorks || !row.actions.downloadDetails}
                    />
                </TableCell>
                }
                <TableCell className={classes.buttonColumn}>
                    <WorkableIconDeleteButton
                        tooltip={row.actions.delete ? Tr('delete') : Tr('delete_repo_disabled')}
                        onClick={onDelete}
                        disabled={iconWorks.anyWorks || !row.actions.delete}
                        working={iconWorks.delete}
                    />
                </TableCell>

                {isPublished && <TableCell className={classes.buttonColumn}>
                    <WorkableIconExpandButton
                        tooltip={hasVersions ? (!row.versions ? Tr('expand_prev_versions') : Tr('collapse_prev_versions')) : Tr('no_prev_versions')}
                        expanded={!!row.versions}
                        onClick={onVersionsExpand}
                        disabled={iconWorks.anyWorks || !hasVersions}
                        working={iconWorks.expand}
                    />
                </TableCell>
                }
            </TableRow>

            {!!row.versions &&
                <TableRow>
                    <TableCell className={classes.versionsCell}></TableCell>
                    <TableCell className={classes.versionsCell} colSpan={isPublished ? 10 : 6}>
                        <SiwzVersionsTable
                            demandId={demandId}
                            rootRowIdentity={row.identity}
                            versions={row.versions}
                            onOpenDownloadHistory={onOpenDownloadHistory}
                            onEditDescriptionFile={onEditDescriptionFile}
                            showShare={showShare}
                        />
                    </TableCell>
                    <TableCell className={classes.versionsCell}></TableCell>
                </TableRow>
            }
        </>
    )
}

SiwzFileTableRow.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    row: PropTypes.shape({
        identity: PropTypes.number.isRequired,
        source: PropTypes.string.isRequired
    }).isRequired,
    index: PropTypes.number.isRequired,
    isPublished: PropTypes.bool.isRequired,
    disabled: PropTypes.bool.isRequired,
    onOpenDownloadHistory: PropTypes.func.isRequired,
    onEditDescriptionFile: PropTypes.func.isRequired,
    onEditNoticeReferenceNumber: PropTypes.func.isRequired,
    showShare: PropTypes.bool.isRequired,
};

export default SiwzFileTableRow;